import { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Badge, Table, Select, Input, Button, MobileCardList } from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { completedTasksStore, CompletedTask } from '../services/completedTasksStore';

interface ReportData {
  id: string;
  gateEntryNumber: string;
  clientName: string;
  invoiceNo: string;
  type: 'Inbound' | 'Outbound';
  productName: string;
  quantity: string;
  unitType?: string;
  warehouseLocation: string;
  date: string;
  time: string;
  batchNumber: string;
  vehicleNumber: string;
  driverName: string;
  dockNumber?: string;
  status: string;
}

export default function MISReport() {
  const { user, hasPermission } = useAuth();
  const canExport = hasPermission('mis-report:export');
  const activeWarehouseId = user?.activeWarehouseId ?? user?.warehouseId ?? null;
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportData, setReportData] = useState<ReportData[]>([]);

  // Load completed tasks and convert to report format
  useEffect(() => {
    const loadData = () => {
      const tasks = completedTasksStore.getTasksForWarehouse(activeWarehouseId);
      const flattenedData: ReportData[] = [];

      tasks.forEach((task: CompletedTask) => {
        task.products.forEach((product, index) => {
          flattenedData.push({
            id: `${task.id}-product-${index}`,
            gateEntryNumber: task.gateEntryNumber,
            clientName: task.clientName,
            invoiceNo: task.invoiceNumber,
            type: task.type,
            productName: product.productDescription,
            quantity: product.quantity,
            unitType: product.unitType,
            warehouseLocation: product.warehouseLocation || 'N/A',
            date: task.completedDate,
            time: task.completedTime,
            batchNumber: product.batchNo || 'N/A',
            vehicleNumber: task.vehicleNumber,
            driverName: task.driverName,
            dockNumber: task.dockNumber,
            status: 'Completed',
          });
        });
      });

      setReportData(flattenedData);
    };

    loadData();

    // Subscribe to updates
    const unsubscribe = completedTasksStore.subscribe(loadData);
    return unsubscribe;
  }, [activeWarehouseId]);

  // Get unique clients for filter
  const uniqueClients = Array.from(new Set(reportData.map(item => item.clientName)));

  // Filter data based on selections
  const filteredData = reportData.filter((item) => {
    if (selectedClient !== 'all' && item.clientName !== selectedClient) return false;
    if (selectedType !== 'all' && item.type !== selectedType) return false;
    if (dateFrom && item.date < dateFrom) return false;
    if (dateTo && item.date > dateTo) return false;
    return true;
  });

  // Calculate summary statistics
  const totalInward = filteredData.filter((item) => item.type === 'Inbound').length;
  const totalOutward = filteredData.filter((item) => item.type === 'Outbound').length;
  const totalQuantityInward = filteredData
    .filter((item) => item.type === 'Inbound')
    .reduce((sum, item) => sum + parseInt(item.quantity || '0'), 0);
  const totalQuantityOutward = filteredData
    .filter((item) => item.type === 'Outbound')
    .reduce((sum, item) => sum + parseInt(item.quantity || '0'), 0);

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Product Name',
      'Item Code', 
      'Description',
      'SKU Code / STN SKU',
      "Customer's SAP Code",
      'Product Name',
      'Batch Number',
      'MFG Date',
      'Expiry Date',
      'Good / Damage Classification',
      'C.DAM',
      'Total Pack Size',
      'Total Weight',
      'Remarks'
    ];
    const csvData = filteredData.map((item) => [
      item.productName, // Product Name
      '', // Item Code - placeholder
      item.productName, // Description (using product name as description)
      '', // SKU Code / STN SKU - placeholder
      '', // Customer's SAP Code - placeholder
      item.productName, // Product Name (duplicate as requested)
      item.batchNumber || '', // Batch Number
      '', // MFG Date - placeholder
      '', // Expiry Date - placeholder
      item.type === 'Inbound' ? 'Good' : 'Damage', // Good / Damage Classification
      '', // C.DAM - placeholder
      item.quantity || '0', // Total Pack Size (using quantity for now)
      '', // Total Weight - placeholder
      `${item.type} - ${item.status} - Vehicle: ${item.vehicleNumber}` // Remarks
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `MIS_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export to Excel (using CSV format with .xls extension)
  const exportToExcel = () => {
    const headers = [
      'Product Name',
      'Item Code', 
      'Description',
      'SKU Code / STN SKU',
      "Customer's SAP Code",
      'Product Name',
      'Batch Number',
      'MFG Date',
      'Expiry Date',
      'Good / Damage Classification',
      'C.DAM',
      'Total Pack Size',
      'Total Weight',
      'Remarks'
    ];
    const csvData = filteredData.map((item) => [
      item.productName, // Product Name
      '', // Item Code - placeholder
      item.productName, // Description (using product name as description)
      '', // SKU Code / STN SKU - placeholder
      '', // Customer's SAP Code - placeholder
      item.productName, // Product Name (duplicate as requested)
      item.batchNumber || '', // Batch Number
      '', // MFG Date - placeholder
      '', // Expiry Date - placeholder
      item.type === 'Inbound' ? 'Good' : 'Damage', // Good / Damage Classification
      '', // C.DAM - placeholder
      item.quantity || '0', // Total Pack Size (using quantity for now)
      '', // Total Weight - placeholder
      `${item.type} - ${item.status} - Vehicle: ${item.vehicleNumber}` // Remarks
    ]);

    const csvContent = [
      headers.join('\t'),
      ...csvData.map((row) => row.join('\t')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `MIS_Report_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  const columns = [
    {
      key: 'gateEntryNumber',
      label: 'GATE ENTRY',
    },
    {
      key: 'clientName',
      label: 'CLIENT NAME',
    },
    {
      key: 'invoiceNo',
      label: 'INVOICE NO',
    },
    {
      key: 'type',
      label: 'TYPE',
      render: (value: string) => (
        <Badge variant={value === 'Inbound' ? 'info' : 'warning'} rounded>
          {value}
        </Badge>
      ),
    },
    {
      key: 'productName',
      label: 'PRODUCT NAME',
    },
    {
      key: 'quantity',
      label: 'QUANTITY',
      render: (value: string, row: ReportData) => (
        <span className="font-semibold">{value} {row.unitType || 'pcs'}</span>
      ),
    },
    {
      key: 'warehouseLocation',
      label: 'LOCATION',
      render: (value: string) => (
        <Badge variant="secondary" rounded>
          {value}
        </Badge>
      ),
    },
    {
      key: 'date',
      label: 'DATE',
      render: (value: string, row: ReportData) => (
        <div>
          <div className="text-sm">{value}</div>
          <div className="text-xs text-gray-500">{row.time}</div>
        </div>
      ),
    },
    {
      key: 'batchNumber',
      label: 'BATCH NO',
    },
    {
      key: 'vehicleNumber',
      label: 'VEHICLE',
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (value: string) => (
        <Badge variant="success" rounded>
          {value}
        </Badge>
      ),
    },
  ];

  return (
    <Layout>
      <div className="w-full max-w-full overflow-hidden">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">MIS Report</h1>
          <p className="text-gray-600 mt-1">Management Information System - Inbound & Outbound Reports</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-arrow-down text-cyan-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">TOTAL INWARD</p>
            <p className="text-3xl font-bold text-gray-900">{totalInward}</p>
            <p className="text-xs text-gray-500 mt-1">Qty: {totalQuantityInward.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-arrow-up text-purple-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">TOTAL OUTWARD</p>
            <p className="text-3xl font-bold text-gray-900">{totalOutward}</p>
            <p className="text-xs text-gray-500 mt-1">Qty: {totalQuantityOutward.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-file-alt text-green-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">TOTAL RECORDS</p>
            <p className="text-3xl font-bold text-gray-900">{filteredData.length}</p>
            <p className="text-xs text-gray-500 mt-1">Filtered results</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-balance-scale text-orange-600 text-xl"></i>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">NET BALANCE</p>
            <p className="text-3xl font-bold text-gray-900">{totalQuantityInward - totalQuantityOutward}</p>
            <p className="text-xs text-gray-500 mt-1">In warehouse</p>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center mb-4">
            <i className="fas fa-filter text-brand-600 mr-2"></i>
            <h3 className="text-lg font-semibold text-gray-900">Filter Reports</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              label="Client"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              options={[
                { value: 'all', label: 'All Clients' },
                ...uniqueClients.map(client => ({ value: client, label: client })),
              ]}
              placeholder="Select Client"
            />

            <Select
              label="Type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'Inbound', label: 'Inbound' },
                { value: 'Outbound', label: 'Outbound' },
              ]}
              placeholder="Select Type"
            />

            <Input
              label="Date From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />

            <Input
              label="Date To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedClient('all');
                setSelectedType('all');
                setDateFrom('');
                setDateTo('');
              }}
            >
              <i className="fas fa-redo mr-2"></i>
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Export Buttons */}
        {canExport && (
        <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold mb-1">Export Reports</h3>
              <p className="text-brand-100 text-sm">Download filtered data in your preferred format</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={exportToExcel}
              >
                <i className="fas fa-file-excel mr-2"></i>
                Export to Excel
              </Button>
              <Button
                variant="secondary"
                onClick={exportToCSV}
              >
                <i className="fas fa-file-csv mr-2"></i>
                Export to CSV
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <i className="fas fa-table text-brand-600 mr-2"></i>
              <h3 className="text-lg font-semibold text-gray-900">Inbound & Outbound Records</h3>
            </div>
            <p className="text-sm text-gray-600">
              Showing {filteredData.length} of {reportData.length} records
            </p>
          </div>

          <div className="hidden lg:block overflow-x-auto w-full">
            <Table columns={columns} data={filteredData} hoverable />
          </div>
          <MobileCardList columns={columns} data={filteredData} />
        </div>
      </div>
    </Layout>
  );
}
